import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { toWapi } from '../util/api';
import Page from '../components/Page';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Avatar,
  Box,
  CircularProgress,
  Chip,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Http as HttpIcon,
  Details as DetailsIcon,
  WhatsApp as WhatsAppIcon,
  Update as VersionIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Message as MessageIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ConnectWithoutContact as ConnectWithoutContactIcon,
  Logout as LogoutIcon,
  AccountBox as AccountBoxIcon
} from '@mui/icons-material';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const PhoneDetail = React.memo(({ product }) => {
  const [status, setStatus] = useState(null);
  const [safety, setSafety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileProxy, setMobileProxy] = useState(false);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [isConversationLoading, setIsConversationLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatError, setChatError] = useState(null);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [debouncedMessage, setDebouncedMessage] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentWebhookUrl, setCurrentWebhookUrl] = useState("");
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [tempWebhookUrl, setTempWebhookUrl] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(null);
  const { phone } = useParams();

  const history = useHistory();

  const { id, settings: { wapi_token } } = product;

  const debouncedSetMessage = useMemo(
    () => debounce(setDebouncedMessage, 300),
    []
  );

  useEffect(() => {
    debouncedSetMessage(message);
    return () => {
      debouncedSetMessage.cancel();
    };
  }, [message, debouncedSetMessage]);

  const fetchPhones = useCallback(async () => {
    try {
      const response = await toWapi(`/api/${id}/listPhones`, wapi_token);
      if (response && response.status === 200 && Array.isArray(response.data)) {
        const phoneData = response.data.find(p => p.id.toString() === phone);
        if (phoneData && phoneData.status === 'active') {
          setIsValidPhone(true);
          if (phoneData.data && phoneData.data.webhook) {
            setCurrentWebhookUrl(phoneData.data.webhook);
          } else {
            setCurrentWebhookUrl("");
          }
        } else {
          setIsValidPhone(false);
        }
      } else {
        setIsValidPhone(false);
      }
    } catch (error) {
      console.error('Error fetching phones:', error);
      setIsValidPhone(false);
    }
  }, [id, wapi_token, phone]);

  const fetchStatus = useCallback(async (id, phone, token) => {
    const response = await toWapi(`/api/${id}/${phone}/status`, token);
    if (response && response.status === 200 && response.data && response.data.success) {
      return response.data;
    }
    throw new Error('Failed to fetch status');
  }, []);

  const fetchSafety = useCallback(async (id, phone, token) => {
    const response = await toWapi(`/api/${id}/${phone}/checkPhoneSafety`, token);
    if (response && response.status === 200 && response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error('Failed to fetch safety data');
  }, []);

  const fetchContacts = useCallback(async (id, phone, token) => {
    const response = await toWapi(`/api/${id}/${phone}/getConversations`, token);
    if (response && response.status === 200 && response.data && response.data.success) {
      return response.data.data
        .reverse()
        .map((contact) => ({
          id: contact._serialized,
          name: contact.name || "+" + contact.user,
          type: contact.server === 'c.us' ? 'chat' : contact.server === 'g.us' ? 'group' : contact.server,
        }));
    }
    throw new Error('Failed to fetch contacts');
  }, []);

  const fetchConversation = useCallback(async (id, phone, contactId, token) => {
    const response = await toWapi(`/api/${id}/${phone}/getConversations/${contactId}`, token);
    if (response && response.status === 200 && response.data && response.data.success) {
      return response.data.data.messages;
    }
    throw new Error('Failed to fetch conversation');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setInitialLoading(true);
      try {
        await fetchPhones();
        if (isValidPhone) {
          const [statusData, safetyData] = await Promise.all([
            fetchStatus(id, phone, wapi_token),
            fetchSafety(id, phone, wapi_token)
          ]);

          setStatus(statusData.status);
          setMobileProxy(statusData.mobile_proxy);
          setSafety(safetyData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, phone, wapi_token, fetchPhones, fetchStatus, fetchSafety, isValidPhone]);

  const handleContactSelect = useCallback(async (contact) => {
    if (!isValidPhone) return;

    setSelectedContact(contact);
    setIsChatLoading(true);
    setChatMessages([]);
    setChatError(null);
    try {
      const messages = await fetchConversation(id, phone, contact.id, wapi_token);
      setChatMessages(messages);
      setTimeout(() => {
        if (chatBoxRef.current) {
          chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
        }
      }, 0);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setChatError('Failed to load messages. Please try again.');
    } finally {
      setIsChatLoading(false);
    }
  }, [id, phone, wapi_token, fetchConversation, isValidPhone]);

  const fetchContactsData = useCallback(async () => {
    if (!isValidPhone) return;

    setIsConversationLoading(true);
    try {
      const contactsData = await fetchContacts(id, phone, wapi_token);
      setConversations(contactsData);
      if (contactsData.length > 0) {
        handleContactSelect(contactsData[0]);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setIsConversationLoading(false);
    }
  }, [id, phone, wapi_token, fetchContacts, handleContactSelect, isValidPhone]);

  useEffect(() => {
    if (isValidPhone && !initialLoading) {
      fetchContactsData();
    }
  }, [isValidPhone, initialLoading, fetchContactsData]);

  const handleEditWebhook = useCallback(() => {
    setTempWebhookUrl(currentWebhookUrl);
    setIsEditingWebhook(true);
  }, [currentWebhookUrl]);

  const handleSaveWebhook = useCallback(async (newWebhookUrl) => {
    if (newWebhookUrl.trim() === currentWebhookUrl) {
      setIsEditingWebhook(false);
      return;
    }

    try {
      const response = await toWapi(`/api/${id}/${phone}/config`, wapi_token, {
        webhook: newWebhookUrl.trim()
      });
      if (response && response.status === 200 && response.data && response.data.success) {
        setCurrentWebhookUrl(newWebhookUrl.trim());
        setIsEditingWebhook(false);
      } else {
        throw new Error('Failed to update webhook');
      }
    } catch (error) {
      console.error('Error updating webhook:', error);
    }
  }, [id, phone, wapi_token, currentWebhookUrl]);

  const handleCancelWebhook = useCallback(() => {
    setTempWebhookUrl(currentWebhookUrl);
    setIsEditingWebhook(false);
  }, [currentWebhookUrl]);

  const handleConversationReload = useCallback(async () => {
    if (!isValidPhone) return;

    setIsConversationLoading(true);
    setIsChatLoading(true);
    setChatMessages([]);
    try {
      const contactsData = await fetchContacts(id, phone, wapi_token);
      setConversations(contactsData);

      if (selectedContact) {
        const messages = await fetchConversation(id, phone, selectedContact.id, wapi_token);
        setChatMessages(messages);
      }
    } catch (err) {
      console.error('Error reloading data:', err);
    } finally {
      setIsConversationLoading(false);
      setIsChatLoading(false);
    }
  }, [id, phone, selectedContact, wapi_token, fetchContacts, fetchConversation, isValidPhone]);

  const handleSendMessage = useCallback(async (message) => {
    if (!isValidPhone || !selectedContact) return;

    try {
      const response = await toWapi(`/api/${id}/${phone}/sendMessage`, wapi_token, {
        to_number: `${selectedContact.id}`,
        type: "text",
        message: message.trim()
      });

      if (response && response.status === 200 && response.data && response.data.success) {
        handleContactSelect(selectedContact);
      } else {
        console.error('Failed to send message:', response);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [id, phone, wapi_token, selectedContact, isValidPhone, handleContactSelect]);

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await toWapi(`/api/${id}/${phone}/logout`, wapi_token);
      handleCloseLogoutDialog();
      history.push('/');
    } catch (error) {
      console.error('Logout error:', error);
      setLogoutError('An error occurred during logout. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const MessageInput = React.memo(({ onSend }) => {
    const [message, setMessage] = useState('');

    const handleSend = useCallback(() => {
      if (message.trim()) {
        onSend(message);
        setMessage('');
      }
    }, [message, onSend]);

    return (
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type a message..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          sx={{ ml: 1 }}
          disabled={!message.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
    );
  });

  const WebhookInput = React.memo(({ currentWebhookUrl, isEditing, onEdit, onSave, onCancel }) => {
    const [tempWebhookUrl, setTempWebhookUrl] = useState(currentWebhookUrl);

    useEffect(() => {
      setTempWebhookUrl(currentWebhookUrl);
    }, [currentWebhookUrl]);

    const handleChange = (e) => {
      setTempWebhookUrl(e.target.value);
    };

    return (
      <>
        <TextField
          variant="outlined"
          fullWidth
          value={tempWebhookUrl}
          onChange={handleChange}
          InputProps={{
            readOnly: !isEditing,
            startAdornment: (
              <InputAdornment position="start">
                <HttpIcon sx={{ color: '#075E54' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#075E54',
              },
              '&:hover fieldset': {
                borderColor: '#075E54',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#075E54',
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={onEdit}
              sx={{
                bgcolor: '#007AFF',
                color: 'white',
                '&:hover': {
                  bgcolor: '#0056b3',
                }
              }}
            >
              Edit Webhook
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => onSave(tempWebhookUrl)}
                sx={{
                  mr: 1,
                  bgcolor: '#007AFF',
                  '&:hover': {
                    bgcolor: '#0056b3',
                  },
                  color: 'white'
                }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{
                  color: '#007AFF',
                  borderColor: '#007AFF',
                  '&:hover': {
                    borderColor: '#0056b3',
                    bgcolor: 'rgba(18, 140, 126, 0.04)',
                  }
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </>
    );
  });

  const WebhookSection = useMemo(() => (
    <StyledPaper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" align="center" sx={{ mb: 3, fontWeight: 'bold', }}>
        Phone Webhook
      </Typography>
      <Box sx={{ mt: 2, width: '100%' }}>
        <WebhookInput
          currentWebhookUrl={currentWebhookUrl}
          isEditing={isEditingWebhook}
          onEdit={handleEditWebhook}
          onSave={handleSaveWebhook}
          onCancel={handleCancelWebhook}
        />
      </Box>
    </StyledPaper>
  ), [currentWebhookUrl, isEditingWebhook, handleEditWebhook, handleSaveWebhook, handleCancelWebhook]);

  const chatBoxRef = useRef(null);
  const renderMessageContent = useMemo(() => (msg) => {
    const renderTextWithLinks = (text) => {
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlPattern);
      return parts.map((part, index) => {
        if (urlPattern.test(part)) {
          return (
            <Link key={index} href={part} target="_blank" rel="noopener noreferrer">
              {part}
            </Link>
          );
        } else {
          return <span key={index}>{part}</span>;
        }
      });
    };

    switch (msg.message.type) {
      case 'text':
        return <Typography variant="body1">{renderTextWithLinks(msg.message.text)}</Typography>;
      case 'image':
        return (
          <Box>
            <img src={msg.message.url} alt="Image" style={{ maxWidth: '100%', maxHeight: '200px' }} />
            <Box>{msg.message.caption && <Typography variant="caption">{msg.message.caption}</Typography>}</Box>
          </Box>
        );
      case 'video':
        return (
          <Box>
            <video src={msg.message.url} controls style={{ maxWidth: '100%', maxHeight: '200px' }} />
            <Box>{msg.message.caption && <Typography variant="caption">{msg.message.caption}</Typography>}</Box>
          </Box>
        );
      case 'ptt':
        return <audio src={msg.message.url} controls />;
      case 'audio':
        return <audio src={msg.message.url} controls />;
      default:
        return <Typography variant="body1">Unsupported message type: {msg.message.type}</Typography>;
    }
  }, []);

  return (
    <Page title={isValidPhone ? `Phone Detail` : ""} icon={isValidPhone ? <DetailsIcon /> : ""}>
      {initialLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100%'
          }}
        >
          <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={40} />
          </Container>
        </Box>
      ) : isValidPhone ? (
        <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3}>
                {loading ? (
                  <CircularProgress size={60} />
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2 }}>
                      <Tooltip title="LOGOUT" arrow>
                        <IconButton
                          color="primary"
                          onClick={handleLogoutClick}
                          sx={{
                            bgcolor: 'rgba(0, 122, 255, 0.08)',
                            '&:hover': {
                              bgcolor: 'rgba(0, 86, 179, 0.12)',
                            }
                          }}
                        >
                          <LogoutIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Avatar
                      src={safety && safety.profile_photo ? safety.profile_photo : undefined}
                      sx={{
                        width: 120,
                        height: 120,
                        mb: 3,
                        bgcolor: '#25D366',
                        border: '4px solid white',
                        boxShadow: '0 0 0 2px #25D366'
                      }}
                    >
                      {!safety || !safety.profile_photo ? <WhatsAppIcon sx={{ fontSize: 60 }} /> : null}
                    </Avatar>
                    <Typography variant="h5" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#075E54' }}>
                      {status ? "+" + status.number : 'Number not available'}
                    </Typography>
                    <Typography variant="subtitle1" align="center" sx={{ color: '#075E54' }}>
                      {safety && safety.isBusiness ? 'WhatsApp Business' : 'WhatsApp'}
                    </Typography>
                    <Chip
                      icon={<WhatsAppIcon style={{ color: 'white' }} />}
                      label={status && status.state && status.state.state === 'CONNECTED' ? 'Connected' : 'Disconnected'}
                      sx={{
                        mt: 2,
                        mb: 2,
                        bgcolor: status && status.state && status.state.state === 'CONNECTED' ? '#25D366' : 'error.main',
                        color: 'common.white',
                        fontWeight: 'bold',
                        px: 3,
                        py: 1,
                        borderRadius: '20px',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                    <Box display="flex" alignItems="center">
                      <VersionIcon sx={{ color: '#075E54', mr: 1 }} />
                      <Typography sx={{ fontWeight: 'bold', }} variant="body1">
                        Version: {status ? status.version : 'N/A'}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mt={2}>
                      <PhoneAndroidIcon sx={{ color: '#075E54', mr: 1 }} />
                      <Typography sx={{ fontWeight: 'bold' }} variant="body1">
                        Mobile Proxy:
                        {mobileProxy ? (
                          <span style={{ fontWeight: 'bold' }}> Used</span>
                        ) : (
                          <span style={{ fontWeight: 'bold' }}> Unused</span>
                        )}
                      </Typography>
                    </Box>
                  </>
                )}
              </StyledPaper>
            </Grid>
            <Grid item xs={12} md={4}>
              <StyledPaper elevation={3} sx={{ pt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                  Phone Info
                </Typography>
                <Grid container spacing={2} sx={{ mt: -2 }}>
                  {[
                    { icon: <GroupIcon />, label: "Groups on Chats", value: safety.groups_on_chats, info: "Number of group chats the user is part of." },
                    { icon: <PersonIcon />, label: "Users on Chats", value: safety.users_on_chats, info: "Number of individual users the person has chatted with." },
                    { icon: <MessageIcon />, label: "Messages to Users", value: safety.user_messages_to_user_count, info: "Total number of messages sent to users." },
                    { icon: <MessageIcon />, label: "Messages to Unique Users", value: safety.user_messages_to_unique_user_count, info: "Number of messages sent to distinct users." },
                    { icon: <MessageIcon />, label: "Messages to Groups", value: safety.user_messages_to_group_count, info: "Total number of messages sent to groups." },
                    { icon: <GroupIcon />, label: "First Message to Group", value: safety.first_group_message, info: "Indicates if the user has sent their first message to a group." },
                    { icon: <ChatIcon />, label: "First Chat", value: safety.first_chat, info: "Indicates if the user has initiated their first chat." },
                    { icon: <MessageIcon />, label: "First Message", value: safety.first_message, info: "Indicates if the user has sent their first message." },
                    { icon: <PersonIcon />, label: "First Message to User", value: safety.first_user_message, info: "Indicates if the user has sent their first direct message to another user." },
                    { icon: <ConnectWithoutContactIcon />, label: "Contact Permission", value: safety.contact_permission, info: "Indicates if the user has granted contact permissions." },
                    { icon: <AccountBoxIcon />, label: "Profile Picture", value: safety.has_profile_picture, info: "Indicates if the user has set a profile picture." },
                    { icon: <PersonIcon />, label: "Booked Contacts", value: safety.booked_contacts_count, info: "Number of contacts the user has saved." },
                  ].map((item, index) => (
                    <Grid item xs={6} sm={4} md={4} key={index}>
                      <Paper elevation={2} sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: { xs: 20, sm: 24 }, color: '#007AFF', mb: 0.5 } })}
                        <Typography variant="body2" align="center" sx={{ fontSize: { xs: '0.75rem', sm: '0.78rem' }, lineHeight: 1.2, mb: 0.5, color: 'black' }}>
                          {item.label}
                        </Typography>
                        {item.label === "Booked Contacts" ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'bold',
                              color: item.value === 0 ? 'error.main' : (item.value > 0 && item.value <= 30) ? 'warning.main' : '#00E676'
                            }}
                          >
                            {item.value}
                          </Typography>
                        ) : (
                          typeof item.value === 'boolean' ? (
                            item.value ? <CheckCircleIcon sx={{ color: '#00E676', fontSize: { xs: '1rem', sm: '1.2rem' } }} /> : <CancelIcon sx={{ color: 'error.main', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                          ) : (
                            item.value ? <CheckCircleIcon sx={{ color: '#00E676', fontSize: { xs: '1rem', sm: '1.2rem' } }} /> : <CancelIcon sx={{ color: 'error.main', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                          )
                        )}
                        <Tooltip
                          title={item.info}
                          placement="bottom"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'white',
                                color: '#333',
                                fontSize: '14px',
                                padding: '8px 12px',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                border: '1px solid #e0e0e0',
                                maxWidth: '250px',
                                '& .MuiTooltip-arrow': {
                                  color: 'white',
                                },
                              },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 5,
                              right: 5,
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              border: '1px solid #3498db',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#3498db',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                lineHeight: 1,
                              }}
                            >
                              i
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </StyledPaper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={2} direction="column">
                <Grid item xs={12}>
                  {WebhookSection}
                </Grid>
                <Grid item xs={12} sx={{ mt: 5 }}>
                  <StyledPaper elevation={3}>
                    <Typography variant="h6" align="center" sx={{ fontWeight: 'bold', }}>API Access Information</Typography>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ mt: 1, }}>
                        <strong>Product ID:</strong> {product.id}
                      </Typography>
                      <Typography variant="body1" textAlign={'left'} sx={{ mt: 1, }}>
                        <strong>Token:</strong> {product.settings.wapi_token}
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          bgcolor: '#007AFF',
                          color: 'white',
                          '&:hover': {
                            bgcolor: '#0056b3',
                          }
                        }}
                        onClick={() => history.push('/developers/documentation')}
                      >
                        Documentation
                      </Button>
                    </Box>
                  </StyledPaper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={3}
                    sx={{
                      height: 'calc(100vh - 300px)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="h6" sx={{ p: 2, backgroundColor: '#445565', color: 'white' }}>
                      Conversations
                    </Typography>
                    <List sx={{ overflowY: 'auto', flexGrow: 1 }}>
                      {isConversationLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <CircularProgress />
                        </Box>
                      ) : (
                        conversations.map((convo) => (
                          <ListItem
                            key={convo.id}
                            button
                            onClick={() => handleContactSelect(convo)}
                            sx={{
                              backgroundColor: selectedContact && selectedContact.id === convo.id ? '#e3f2fd' : 'inherit',
                            }}
                          >
                            <ListItemText primary={convo.name} />
                            <Chip
                              icon={convo.type === 'group' ? <GroupIcon /> : <ChatIcon />}
                              label={convo.type.toUpperCase()}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={3}
                    sx={{
                      height: 'calc(100vh - 300px)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{
                      p: 2,
                      backgroundColor: '#445565',
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography variant="h6">
                        Chat {selectedContact && `- ${selectedContact.name}`}
                      </Typography>
                      <Button
                        onClick={handleConversationReload}
                        color="inherit"
                        startIcon={<RefreshIcon />}
                        disabled={isConversationLoading || isChatLoading}
                      >
                        Reload
                      </Button>
                    </Box>
                    <Box
                      ref={chatBoxRef}
                      sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}
                    >
                      {isChatLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                          <CircularProgress />
                        </Box>
                      ) : chatError ? (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
                          <Typography color="error" gutterBottom>{chatError}</Typography>
                        </Box>
                      ) : (
                        chatMessages.map((msg) => (
                          <Box
                            key={msg.message.id}
                            sx={{
                              mb: 2,
                              display: 'flex',
                              justifyContent: msg.fromMe ? 'flex-end' : 'flex-start',
                            }}
                          >
                            <Paper
                              elevation={1}
                              sx={{
                                p: 1,
                                maxWidth: '70%',
                                bgcolor: msg.fromMe ? 'primary.light' : 'grey.100',
                              }}
                            >
                              {renderMessageContent(msg)}
                            </Paper>
                          </Box>
                        ))
                      )}
                    </Box>
                    <MessageInput onSend={handleSendMessage} />
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      ) : (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            An error occurred
          </Typography>
          <Typography variant="body1" paragraph>
            Please make sure that this Phone ID is yours and that it is active.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push('/')}
          >
            Go to Homepage
          </Button>
        </Container>
      )}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogoutDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Are you sure you want to log out?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This will end your current session and you will be disconnected from the phone.
          </DialogContentText>
          {isLoggingOut && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {logoutError && (
            <Typography color="error" sx={{ mt: 2 }}>
              {logoutError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoutDialog} disabled={isLoggingOut}>No</Button>
          <Button onClick={handleLogout} autoFocus disabled={isLoggingOut}>
            {isLoggingOut ? 'Logging out...' : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
});

const stateToProps = (state) => ({
  access: state.access,
  location: state.router.location,
  product: state.product.maytapi,
});

export default connect(stateToProps)(PhoneDetail);